import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import type { MekkeBookletResponseDto } from "@tabipla/shared";
import { cn } from "@tabipla/ui/lib/utils";
import { getVisibilityConfig } from "../../../utils/style-config";

interface BookletCardProps {
  booklet: MekkeBookletResponseDto;
}

export function BookletCard({ booklet }: BookletCardProps) {
  const { t } = useTranslation();
  const config = getVisibilityConfig(booklet.visibility);
  const VisibilityIcon = config.icon;

  return (
    <Link
      to={`/booklets/${booklet.id}`}
      className="group block cursor-pointer overflow-hidden rounded-2xl bg-card shadow-md ring-1 ring-black/5 transition-all hover:shadow-xl hover:ring-black/10 active:scale-[0.98]"
    >
      {/* Cover Image */}
      <div className="relative h-32 overflow-hidden sm:h-48">
        {booklet.coverImageUrl ? (
          <img
            src={booklet.coverImageUrl}
            alt={booklet.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          /* Decorative placeholder with layered circles */
          <div className="relative h-full w-full" style={{ background: config.gradientStyle }}>
            {/* Decorative circles */}
            <div
              className="absolute -right-8 -top-8 h-32 w-32 rounded-full"
              style={{ backgroundColor: config.decorColor1 }}
            />
            <div
              className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full"
              style={{ backgroundColor: config.decorColor2 }}
            />
            <div
              className="absolute bottom-8 right-8 h-16 w-16 rounded-full"
              style={{ backgroundColor: config.decorColor3 }}
            />
            {/* Spot count indicator */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-0.5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                <span className="text-4xl font-extrabold">{booklet.spotCount}</span>
                <span className="text-xs font-bold uppercase tracking-widest">spots</span>
              </div>
            </div>
          </div>
        )}
        {/* Visibility badge */}
        <div
          className={cn(
            "absolute left-3 top-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm",
            config.badgeBg
          )}
        >
          <VisibilityIcon className="h-3 w-3" />
          <span>{t(`booklets.visibility.${booklet.visibility}`)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
          {booklet.title}
        </h3>
        {booklet.description && (
          <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{booklet.description}</p>
        )}

        {/* Footer with spot count (only if cover image exists, otherwise shown in placeholder) */}
        {booklet.coverImageUrl && (
          <div className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {booklet.spotCount > 0
              ? t("booklets.spots", { count: booklet.spotCount })
              : t("booklets.noSpots")}
          </div>
        )}
      </div>
    </Link>
  );
}
